import React, { useState, useEffect, useLayoutEffect, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { CartContext } from '../../../context/CartContext';
import apiService from '../../../services/api';
import './CountdownBar.css';

// Pages that never show the bar
const EXCLUDED_PATHS = [
  '/general-policies',
  '/subscription-policies',
  '/privacy-policies',
  '/marketing-policies',
  '/request-item',
  '/login',
  '/register',
  '/verify-email',
];

// Header bottom per breakpoint — mirrors Header.css
const getHeaderHeight = () => {
  const w = window.innerWidth;
  const notif = document.body.classList.contains('has-notification') ? 29 : 0;
  if (w <= 576) return 60 + notif;
  if (w <= 640) return 70 + notif;
  if (w <= 992) return 88 + notif;
  return 124 + notif; // 88 main + 36 nav
};

const CountdownBar = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { openOrder } = useContext(CartContext);

  const [postingDate, setPostingDate] = useState(null);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  // Only used on homepage to track whether the RequestBanner has scrolled past
  const [scrolledPastBanner, setScrolledPastBanner] = useState(false);

  const isHomePage = location.pathname === '/';
  const isExcluded = EXCLUDED_PATHS.some((p) => location.pathname.startsWith(p));

  // Bar is visible when: not excluded, AND (not on homepage OR scrolled past the RequestBanner)
  // This is computed synchronously — no stale state between route changes
  const visible = !isExcluded && (!isHomePage || scrolledPastBanner);

  const hasOpenOrder = isAuthenticated && !!openOrder;
  const labelLines = hasOpenOrder ? ['ADD TO ORDER ', 'FOR'] : ['ORDERS ', 'CLOSING IN'];

  // Fetch posting date once
  useEffect(() => {
    apiService.config.getPostingDate().then((res) => {
      if (res.data.success && res.data.data.postingDate) {
        setPostingDate(new Date(res.data.data.postingDate));
      }
    }).catch(() => {});
  }, []);

  // Countdown tick
  useEffect(() => {
    if (!postingDate) return;
    const tick = () => {
      const diff = postingDate - new Date();
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff / 3600000) % 24),
        minutes: Math.floor((diff / 60000) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [postingDate]);

  // On homepage: track scroll past RequestBanner; reset immediately on route change
  useEffect(() => {
    if (!isHomePage) {
      setScrolledPastBanner(false);
      return;
    }

    const check = () => {
      const el = document.getElementById('request-banner');
      if (!el) { setScrolledPastBanner(true); return; }
      setScrolledPastBanner(el.getBoundingClientRect().bottom <= getHeaderHeight());
    };

    window.addEventListener('scroll', check, { passive: true });
    check();
    return () => window.removeEventListener('scroll', check);
  }, [isHomePage]);

  // Manage body class synchronously (before paint) to avoid margin flash
  useLayoutEffect(() => {
    if (!isExcluded && !isHomePage) {
      document.body.classList.add('has-countdown-bar');
    } else {
      document.body.classList.remove('has-countdown-bar');
    }
    return () => document.body.classList.remove('has-countdown-bar');
  }, [isExcluded, isHomePage]);

  if (!visible) return null;

  const fmt = (n) => String(n).padStart(2, '0');

  return (
    <div className="countdown-bar">
      <div className="countdown-bar-inner">
        <span className="countdown-bar-label">{labelLines[0]}<br />{labelLines[1]}</span>
        <div className="countdown-bar-box">
          <div className="countdown-bar-labels">
            <span className="countdown-bar-unit-label">DAYS</span>
            <span className="countdown-bar-unit-label">HRS</span>
            <span className="countdown-bar-unit-label">MINS</span>
            <span className="countdown-bar-unit-label">SECS</span>
          </div>
          <div className="countdown-bar-display">
            <span className="countdown-bar-number">{fmt(timeLeft.days)}</span>
            <span className="countdown-bar-sep">:</span>
            <span className="countdown-bar-number">{fmt(timeLeft.hours)}</span>
            <span className="countdown-bar-sep">:</span>
            <span className="countdown-bar-number">{fmt(timeLeft.minutes)}</span>
            <span className="countdown-bar-sep">:</span>
            <span className="countdown-bar-number">{fmt(timeLeft.seconds)}</span>
          </div>
        </div>
        <Link to="/category/all" className="countdown-bar-btn" onClick={() => window.scrollTo(0, 0)}>SHOP NOW</Link>
      </div>
    </div>
  );
};

export default CountdownBar;
